/**
 * Business Claim API Service
 * SESSION_329: Non-Existent Business Claim Workflow
 *
 * Handles HTTP requests to the business claim endpoints.
 * Backend endpoints: /api/business-claims
 */

import { apiClient } from './ApiClient';
import type {
  BusinessClaimsPagedResponse,
  UserClaimsResponse,
  SubmitBusinessClaimCommand,
  SubmitNewBusinessClaimCommand,
  SubmitClaimResponse,
  VerifyClaimCodeCommand,
  VerifyClaimCodeResponse,
  ApproveBusinessClaimCommand,
  ApproveNewBusinessClaimCommand,
  RejectBusinessClaimCommand,
  AdminClaimActionResponse
} from '../../domain/entities/BusinessClaim';

/**
 * Query parameters for pending business claims (admin)
 */
export interface GetPendingBusinessClaimsParams {
  pageNumber?: number;
  pageSize?: number;
}

/**
 * API service for business claim operations
 */
export class BusinessClaimApi {
  /**
   * Submit a claim for an existing business
   * Endpoint: POST /api/business-claims
   *
   * @param command - Business claim submission command
   * @returns Submission result with claim ID
   */
  async submitClaim(command: SubmitBusinessClaimCommand): Promise<SubmitClaimResponse> {
    return apiClient.post<SubmitClaimResponse>('/api/business-claims', command);
  }

  /**
   * Submit a claim for a non-existent business (new business request)
   * Endpoint: POST /api/business-claims/new-business
   * SESSION_328: Non-Existent Business Claim Workflow
   *
   * @param command - New business claim submission command
   * @returns Submission result with claim ID and next steps
   */
  async submitNewBusinessClaim(command: SubmitNewBusinessClaimCommand): Promise<SubmitClaimResponse> {
    return apiClient.post<SubmitClaimResponse>('/api/business-claims/new-business', command);
  }

  /**
   * Verify business claim with verification code
   * Endpoint: POST /api/business-claims/{id}/verify
   *
   * @param claimId - The claim ID to verify
   * @param command - Verification code command
   * @returns Verification result
   */
  async verifyClaimCode(
    claimId: string,
    command: VerifyClaimCodeCommand
  ): Promise<VerifyClaimCodeResponse> {
    return apiClient.post<VerifyClaimCodeResponse>(
      `/api/business-claims/${claimId}/verify`,
      command
    );
  }

  /**
   * Get current user's business claims
   * Endpoint: GET /api/business-claims
   *
   * @returns User's business claims
   */
  async getMyClaims(): Promise<UserClaimsResponse> {
    return apiClient.get<UserClaimsResponse>('/api/business-claims');
  }

  /**
   * Get pending business claims for admin review
   * Endpoint: GET /api/business-claims/pending
   *
   * @param params - Pagination parameters
   * @returns Paginated list of pending claims
   */
  async getPendingClaims(params?: GetPendingBusinessClaimsParams): Promise<BusinessClaimsPagedResponse> {
    return apiClient.get<BusinessClaimsPagedResponse>('/api/business-claims/pending', { params });
  }

  /**
   * Get rejected business claims for admin re-review
   * Endpoint: GET /api/business-claims/rejected
   * SESSION_349: Rejected Claim Management
   *
   * @param params - Pagination parameters
   * @returns Paginated list of rejected claims
   */
  async getRejectedClaims(params?: GetPendingBusinessClaimsParams): Promise<BusinessClaimsPagedResponse> {
    return apiClient.get<BusinessClaimsPagedResponse>('/api/business-claims/rejected', { params });
  }

  /**
   * Approve an existing business claim (Admin only)
   * Endpoint: POST /api/business-claims/{id}/approve
   *
   * @param claimId - The claim ID to approve
   * @param command - Optional admin notes
   * @returns Approval result
   */
  async approveClaim(
    claimId: string,
    command?: ApproveBusinessClaimCommand
  ): Promise<AdminClaimActionResponse> {
    return apiClient.post<AdminClaimActionResponse>(
      `/api/business-claims/${claimId}/approve`,
      command ?? {}
    );
  }

  /**
   * Approve a new business claim and create the business (Admin only)
   * Endpoint: POST /api/business-claims/{id}/approve-new-business
   * SESSION_328: Non-Existent Business Claim Workflow
   *
   * @param claimId - The claim ID to approve
   * @param command - Approval details including region ID
   * @returns Approval result
   */
  async approveNewBusinessClaim(
    claimId: string,
    command: ApproveNewBusinessClaimCommand
  ): Promise<AdminClaimActionResponse> {
    return apiClient.post<AdminClaimActionResponse>(
      `/api/business-claims/${claimId}/approve-new-business`,
      command
    );
  }

  /**
   * Reject a business claim (Admin only)
   * Endpoint: POST /api/business-claims/{id}/reject
   *
   * @param claimId - The claim ID to reject
   * @param command - Rejection reason (required)
   * @returns Rejection result
   */
  async rejectClaim(
    claimId: string,
    command: RejectBusinessClaimCommand
  ): Promise<AdminClaimActionResponse> {
    return apiClient.post<AdminClaimActionResponse>(
      `/api/business-claims/${claimId}/reject`,
      command
    );
  }
}

// Export singleton instance
export const businessClaimApi = new BusinessClaimApi();
